import { useState, useEffect } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import { GfmMarkdownContent } from './GfmMarkdownContent';

/**
 * Annotation keys used to resolve which GitHub repo/path to fetch the README from.
 */
const GITHUB_PROJECT_SLUG = 'github.com/project-slug';
const GITHUB_README_PATH = 'github.com/project-readme-path';

/**
 * Response shape from the github-readme backend plugin.
 */
interface ReadmeResponse {
  content: string;
  htmlUrl: string;
}

/**
 * Props for the ReadmeCard component.
 */
interface ReadmeCardProps {
  /** Card title. Defaults to " " (hidden via CSS with readme-card-no-header). */
  title?: string;
}

/**
 * Check whether the current entity has a `github.com/project-slug` annotation,
 * making the ReadmeCard available for rendering.
 *
 * Use as an `EntitySwitch` / `if` guard — drop-in replacement for
 * `isGithubInsightsAvailable` from the Roadie plugin.
 */
export function isReadmeCardAvailable(entity: {
  metadata: { annotations?: Record<string, string> };
}): boolean {
  return Boolean(entity?.metadata?.annotations?.[GITHUB_PROJECT_SLUG]);
}

/**
 * A card that renders a GitHub repository README using the server-side
 * GitHub integration token (via the `github-readme` backend plugin).
 *
 * Users do NOT need to be authenticated with GitHub — the backend fetches
 * content using `integrations.github[].token` from app-config.
 *
 * The component reads:
 *   - `github.com/project-slug`        → `owner/repo`
 *   - `github.com/project-readme-path` → optional custom file path
 *
 * Markdown is rendered with GitHub-Flavored Markdown via
 * `GfmMarkdownContent` which supports GFM alert/callout panels.
 */
export function ReadmeCard({ title = ' ' }: ReadmeCardProps) {
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);

  const projectSlug = entity.metadata.annotations?.[GITHUB_PROJECT_SLUG];
  const readmePath = entity.metadata.annotations?.[GITHUB_README_PATH];

  const [data, setData] = useState<ReadmeResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;

    async function fetchReadme() {
      if (!projectSlug) {
        setLoading(false);
        return;
      }

      const [owner, repo] = projectSlug.split('/');
      if (!owner || !repo) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const queryParams = readmePath
          ? `?path=${encodeURIComponent(readmePath)}`
          : '';
        const url = `${backendUrl}/api/github-readme/readme/${encodeURIComponent(
          owner,
        )}/${encodeURIComponent(repo)}${queryParams}`;

        const response = await fetchApi.fetch(url);

        if (cancelled) return;

        if (!response.ok) {
          if (response.status === 404) {
            setData(undefined);
            setLoading(false);
            return;
          }
          throw new Error(`Failed to fetch README: ${response.statusText}`);
        }

        const result = (await response.json()) as ReadmeResponse;
        if (!cancelled) {
          setData(result);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchReadme();
    return () => {
      cancelled = true;
    };
  }, [projectSlug, readmePath, fetchApi, configApi]);

  if (loading) {
    return (
      <InfoCard title={title}>
        <Progress />
      </InfoCard>
    );
  }

  if (error || !data) {
    // Render an empty card to preserve the grid layout when the README
    // cannot be loaded (e.g. 404 or network error).  Returning null would
    // leave an 8-column hole in the EntitySwitch branch.
    return <InfoCard title={title} />;
  }

  // Rewrite relative image URLs to point at raw.githubusercontent.com
  const [owner, repo] = projectSlug!.split('/');
  const transformImageUri = (src: string): string => {
    if (
      src.startsWith('http://') ||
      src.startsWith('https://') ||
      src.startsWith('data:')
    ) {
      return src;
    }
    const cleanPath = src.replace(/^\.\//, '');
    return `https://raw.githubusercontent.com/${owner}/${repo}/HEAD/${cleanPath}`;
  };

  // Rewrite relative link URLs to point at the repo on GitHub
  const transformLinkUri = (href: string): string => {
    if (
      href.startsWith('http://') ||
      href.startsWith('https://') ||
      href.startsWith('#')
    ) {
      return href;
    }
    const cleanPath = href.replace(/^\.\//, '');
    return `https://github.com/${owner}/${repo}/blob/HEAD/${cleanPath}`;
  };

  return (
    <InfoCard title={title}>
      <GfmMarkdownContent
        content={data.content}
        transformImageUri={transformImageUri}
        transformLinkUri={transformLinkUri}
      />
    </InfoCard>
  );
}
